import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, LogOut, User, ChevronDown, BookOpen, ClipboardList } from 'lucide-react';
import { navLinks, siteConfig } from '../data/mock';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { user, logout, getQuizResults } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  const isHomePage = location.pathname === '/';
  
  // Check if user has quiz results or course access
  const quizResults = user ? getQuizResults() : [];
  const hasStartedQuiz = quizResults.length > 0;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (link) => {
    if (link.isExternal) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
    } else if (link.isRoute) {
      navigate(link.href);
    } else if (isHomePage) {
      const element = document.querySelector(link.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/' + link.href);
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? isDark 
            ? 'bg-stone-950/95 backdrop-blur-md shadow-lg' 
            : 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="flex items-center group"
            data-testid="header-logo"
          >
            <img 
              src="/logo.svg" 
              alt={siteConfig.name}
              className="h-8 sm:h-10 w-auto transition-opacity group-hover:opacity-80"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`hover:text-amber-500 transition-colors font-medium ${
                  link.isRoute && location.pathname === link.href
                    ? 'text-amber-500'
                    : isDark ? 'text-stone-300' : 'text-stone-600'
                }`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-stone-800 hover:bg-stone-700 text-amber-400' 
                  : 'bg-stone-100 hover:bg-stone-200 text-amber-600'
              }`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Auth buttons */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                {/* User Menu Button */}
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                    isDark 
                      ? 'bg-stone-800 hover:bg-stone-700' 
                      : 'bg-stone-100 hover:bg-stone-200'
                  }`}
                  data-testid="user-menu-btn"
                >
                  <User size={16} className={isDark ? 'text-amber-500' : 'text-amber-600'} />
                  <span className={`text-sm font-medium ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                    {user.name?.split(' ')[0]}
                  </span>
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform ${isUserMenuOpen ? 'rotate-180' : ''} ${isDark ? 'text-stone-500' : 'text-stone-400'}`} 
                  />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div 
                    className={`absolute right-0 mt-2 w-56 rounded-xl border shadow-xl overflow-hidden ${
                      isDark 
                        ? 'bg-stone-900 border-stone-800' 
                        : 'bg-white border-stone-200'
                    }`}
                    data-testid="user-dropdown-menu"
                  >
                    {/* User Info */}
                    <div className={`px-4 py-3 border-b ${isDark ? 'border-stone-800' : 'border-stone-100'}`}>
                      <p className={`text-sm font-medium ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                        {user.name}
                      </p>
                      <p className={`text-xs truncate ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        {user.email}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      {/* Quiz Option */}
                      <button
                        onClick={() => handleMenuItemClick('/quiz')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                          isDark 
                            ? 'hover:bg-stone-800 text-stone-300' 
                            : 'hover:bg-stone-50 text-stone-600'
                        }`}
                        data-testid="menu-quiz-btn"
                      >
                        <ClipboardList size={18} className={isDark ? 'text-amber-500' : 'text-amber-600'} />
                        <div className="text-left">
                          <span className="block text-sm font-medium">
                            {hasStartedQuiz ? 'Retake Quiz' : 'Take Quiz'}
                          </span>
                          {hasStartedQuiz && (
                            <span className={`text-xs ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                              {quizResults.length} result{quizResults.length !== 1 ? 's' : ''} saved
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Course Option */}
                      <button
                        onClick={() => handleMenuItemClick('/course')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                          isDark 
                            ? 'hover:bg-stone-800 text-stone-300' 
                            : 'hover:bg-stone-50 text-stone-600'
                        }`}
                        data-testid="menu-course-btn"
                      >
                        <BookOpen size={18} className={isDark ? 'text-green-500' : 'text-green-600'} />
                        <div className="text-left">
                          <span className="block text-sm font-medium">7-Day Course</span>
                          <span className={`text-xs ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                            Continue learning
                          </span>
                        </div>
                      </button>
                    </div>

                    {/* Sign Out */}
                    <div className={`py-1 border-t ${isDark ? 'border-stone-800' : 'border-stone-100'}`}>
                      <button
                        onClick={handleLogout}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                          isDark 
                            ? 'hover:bg-stone-800 text-red-400' 
                            : 'hover:bg-stone-50 text-red-500'
                        }`}
                        data-testid="menu-logout-btn"
                      >
                        <LogOut size={18} />
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold px-6"
              >
                Take Quiz
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all ${
                isDark 
                  ? 'bg-stone-800 text-amber-400' 
                  : 'bg-stone-100 text-amber-600'
              }`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`md:hidden backdrop-blur-md border-t ${isDark ? 'bg-stone-950/98 border-stone-800' : 'bg-white/98 border-stone-200'}`}>
          <nav className="flex flex-col px-4 py-6 gap-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`hover:text-amber-500 transition-colors font-medium text-left py-2 ${
                  link.isRoute && location.pathname === link.href
                    ? 'text-amber-500'
                    : isDark ? 'text-stone-300' : 'text-stone-600'
                }`}
              >
                {link.name}
              </button>
            ))}
            
            {user ? (
              <div className={`pt-4 border-t space-y-3 ${isDark ? 'border-stone-800' : 'border-stone-200'}`}>
                {/* User Info */}
                <div className={`flex items-center gap-2 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                  <User size={16} />
                  <span className="text-sm">Signed in as {user.name}</span>
                </div>

                {/* Quiz Button */}
                <button
                  onClick={() => handleMenuItemClick('/quiz')}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    isDark 
                      ? 'bg-stone-800 hover:bg-stone-700 text-stone-300' 
                      : 'bg-stone-100 hover:bg-stone-200 text-stone-600'
                  }`}
                >
                  <ClipboardList size={18} className={isDark ? 'text-amber-500' : 'text-amber-600'} />
                  <span className="font-medium">{hasStartedQuiz ? 'Retake Quiz' : 'Take Quiz'}</span>
                </button>

                {/* Course Button */}
                <button
                  onClick={() => handleMenuItemClick('/course')}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    isDark 
                      ? 'bg-stone-800 hover:bg-stone-700 text-stone-300' 
                      : 'bg-stone-100 hover:bg-stone-200 text-stone-600'
                  }`}
                >
                  <BookOpen size={18} className={isDark ? 'text-green-500' : 'text-green-600'} />
                  <span className="font-medium">7-Day Course</span>
                </button>

                {/* Sign Out Button */}
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className={`w-full ${isDark ? 'border-stone-700 text-red-400' : 'border-stone-300 text-red-500'}`}
                >
                  <LogOut size={16} className="mr-2" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => {
                  navigate('/auth');
                  setIsMobileMenuOpen(false);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold mt-4"
              >
                Take Quiz
              </Button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
